import React from "react";
import { Alert, Paper, Typography } from "@mui/material";
import { spacing } from "../../styles/theme";
import type { SpecsFormPageSchema } from "./specsForm/types";
import SpecsFormEntriesGrid from "./specsForm/SpecsFormEntriesGrid";
import SpecsFormEntryRenderer from "./specsForm/SpecsFormEntryRenderer";
import { ProductVersionShippingOptionModel } from "./specsModel/productMetadata/ProductVersionShippingOptionModel";
import { ProductVersionShippingPriceBracketModel } from "./specsModel/productMetadata/ProductVersionShippingPriceBracketModel";
import { ProductVersionShippingPriceModel } from "./specsModel/productMetadata/ProductVersionShippingPriceModel";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsModelContextValue } from "./SpecsModelContext";
import { useProductVersionMetadataModel } from "./useProductVersionMetadataModel";

const shippingSchema: SpecsFormPageSchema = {
  sections: [
    {
      title: "Shipping",
      entries: [
        {
          kind: "array",
          path: "shipping",
          title: "Shipping Tiers",
          addButtonLabel: "Add shipping tiers",
          clipboardLabel: "shipping tiers",
          enableClipboard: true,
          serializeClipboardItems: (items) =>
            (items as ProductVersionShippingOptionModel[]).map((item) =>
              item.save()
            ),
          restoreClipboardItems: (snapshots) =>
            snapshots.map((snapshot) =>
              new ProductVersionShippingOptionModel().restore(snapshot)
            ),
          unframed: true,
          createItem: () => new ProductVersionShippingOptionModel(),
          getItemLabel: (item, index) => {
            const tier =
              item && typeof item === "object" && "tier" in item
                ? String((item as { tier?: string }).tier ?? "")
                : "";
            return tier || `Shipping Tier ${index + 1}`;
          },
          columns: 2,
          entries: [
            { kind: "field", path: "tier" },
            { kind: "field", path: "hideDelayAfter" },
            { kind: "field", path: "minWeeks" },
            { kind: "field", path: "maxWeeks" },
            {
              kind: "array",
              path: "priceBrackets",
              title: "Price Brackets",
              createItem: () => new ProductVersionShippingPriceBracketModel(),
              getItemLabel: (_item, index) => `Bracket ${index + 1}`,
              columns: 5,
              colSpan: 2,
              entries: [
                { kind: "field", path: "orderBelow" },
                {
                  kind: "object",
                  path: "price",
                  title: "Price",
                  createValue: () => new ProductVersionShippingPriceModel(),
                  removable: false,
                  columns: 2,
                  colSpan: 2,
                  entries: [
                    { kind: "field", path: "cad" },
                    { kind: "field", path: "usd" },
                  ],
                },
                { kind: "field", path: "distributorOnly", alignSelf: "center" },
                { kind: "field", path: "adminOnly", alignSelf: "center" },
                { kind: "field", path: "extra", colSpan: 2 },
              ],
            },
            { kind: "field", path: "extra", colSpan: 2 },
          ],
        },
      ],
    },
  ],
};

export default function SpecsShippingPage() {
  const {
    editor: { identity, rawVersionMetadata, updateRawVersionMetadata },
  } = useSpecsEditorContextValue();
  const { specsModel } = useSpecsModelContextValue();
  const metadataModel = useProductVersionMetadataModel({
    rawVersionMetadata,
    updateRawVersionMetadata,
  });

  if (!specsModel) {
    return (
      <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
        <Typography variant="h2" sx={{ mb: 3 }}>
          Shipping
        </Typography>
        <Alert severity="info">
          Select a product and version to edit shipping metadata.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
      <Typography variant="h2" sx={{ mb: 0.5 }}>
        Shipping
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Shipping tiers and price brackets.
      </Typography>
      <SpecsFormEntriesGrid
        entries={shippingSchema.sections[0].entries}
        basePath=""
        columns={shippingSchema.sections[0].columns}
        renderEntry={(entry) => (
          <SpecsFormEntryRenderer
            model={metadataModel}
            entry={entry}
            basePath=""
            debugPathPrefix="window.specs.metadataModel"
            imageUploadContext={{
              specType: "product",
              specName: identity.productName || undefined,
            }}
            clickToEditFields
          />
        )}
      />
    </Paper>
  );
}
